/*
 * 共通関数
 * コンポーネントで使用するにはmixinsで読み込む
 * import cf from '@/mixins/commonFunctions';
 * mixins: [cf],
 */

import moment from 'moment';
import { cloneDeep } from 'lodash';
import axios from '../plugins/axios';

// JST の UTC オフセット（分）。JST は DST が無いため固定オフセットで常に正確に表現でき、
// moment-timezone（'Asia/Tokyo'）を持ち込む必要がない。端末 TZ 非依存で JST 壁時計を扱う際に用いる。
export const JST_UTC_OFFSET_MIN = 540;

// 管理者の所属等（F 情報）を見せてよい閲覧者のロール（学校管理者 11/12・事務局 31）。
// 投稿者の氏名の横（methods.getPostUserAffiliation）と、メッセージの To の相手（mixins/messageTo）で共有する
export const AFFILIATION_VIEWER_ACCOUNT_TYPES = [11, 12, 31];

/**
 * schoolAdmins の所属/職階/役割を「所属／職階／役割」にする（カッコ無し）。3 つとも空なら「要更新」。
 * 氏名と別の行に出す To の候補のカードで使う（区切りのカッコが要らない）。
 * @param {{department?: string|null, position?: string|null, role?: string|null}|null} schoolAdmin
 * @returns {string}
 */
export const formatSchoolAdminAffiliationBare = (schoolAdmin) => {
  const values = schoolAdmin
    ? ['department', 'position', 'role']
      .map((key) => schoolAdmin[key])
      .filter((value) => value != null && String(value).trim() !== '')
    : [];
  return values.length ? values.join('／') : '要更新';
};

/**
 * schoolAdmins の所属/職階/役割を「（所属／職階／役割）」にする。3 つとも空なら「（要更新）」。
 * 投稿者の氏名の横と To の相手で書き方をそろえるため、ロールや閲覧者の判定は持たせず整形だけをここに置く。
 * 氏名に続けて同じ行に書くので、カッコが区切りになる。
 * @param {{department?: string|null, position?: string|null, role?: string|null}|null} schoolAdmin
 * @returns {string}
 */
export const formatSchoolAdminAffiliation = (schoolAdmin) => `（${formatSchoolAdminAffiliationBare(schoolAdmin)}）`;

// 相談 1 枠の長さ（分）。予約枠は日本時間の毎時 0 分に始まる 45 分間。
const COUNSELING_SLOT_MINUTES = 45;

// ゾーン付き ISO（末尾が Z か ±hh:mm）。schedules.date 等はこの形で届く。
// naive JST（created_at 等）は端末 TZ で解釈されて日本時間へ換算できないので、日本時間の併記には使わない。
const isIsoInstant = (value) => typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})$/.test(value);

// methods.jstSlotLabel の本体。cf.methods.jstSlotSuffix(...) のようにコンポーネント外からも呼ばれるので、
// this を介さずに呼べる形で持つ
const formatJstSlot = (value) => {
  if (!isIsoInstant(value)) return '';
  const local = moment(value);
  // 正規表現は通るが日時として不正な値で「Invalid date」を画面に出さない
  if (!local.isValid() || local.utcOffset() === JST_UTC_OFFSET_MIN) return '';
  const jst = local.clone().utcOffset(JST_UTC_OFFSET_MIN);
  const end = jst.clone().add(COUNSELING_SLOT_MINUTES, 'minutes');
  return `${jst.format('YYYY年MM月DD日（ddd） HH:mm')} 〜 ${end.format('HH:mm')}`;
};

// 曜日の日本語表記
moment.locale('ja', {
  weekdays: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'],
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土'],
});

export default {
  /**
   * localStorage[${key}]の値を返却
   * @param  str key localStorageの対象キー
   * @return obj jsonパース済みオブジェクト
   */
  getLocalStorage(key) {
    let result;
    if (localStorage[key]) {
      result = JSON.parse(localStorage[key]);
    }
    return result;
  },


  /**
   * ローカルストレージに保存
   * @param obj 既存データにマージしたいオブジェクト
   * @param str key localStorageの対象キー
   */
  saveLocalStorage(obj, key = '') {
    if (!key) return;
    const ls = this.getLocalStorage(key);
    const save = ls ? { ...ls, ...obj } : obj;
    const stringify = JSON.stringify(save);
    localStorage.setItem(key, stringify);
  },


  /**
   * localStorage削除
   * @param key localStorageから削除するキー
   */
  deleteLocalStorage(key, key2 = null) {
    if (!key) return;
    if (localStorage[key]) {
      if (!key2) {
        localStorage.removeItem(key);
        console.log(`localStorage[${key}]を削除しました`);
      } else {
        // 特定の子階層のみを削除

        // 指定キー以外を一時ストック
        const stock = {};
        const target = JSON.parse(localStorage[key]);

        const keys = Object.keys(target);
        for (let i = 0; i < keys.length; i += 1) {
          const k = keys[i];
          if (k !== key2) stock[k] = target[k];
        }
        // 一度localstorage[key]ごと削除して
        localStorage.removeItem(key);
        // ストックしておいたkey2の除外データを保存
        this.saveLocalStorage(stock, key);
        console.log(`localStorage[${key}]の[${key2}]を削除しました`);
      }
    } else {
      console.log(`localStorage[${key}]は存在しません`);
    }
  },

  saveTabState(query) {
    const tabName = query.tab || '';
    this.saveLocalStorage({
      saveState: {
        tab: tabName,
      },
    }, 'wtt');
  },

  /** LSにtab情報があれば削除 */
  deleteTabState() {
    const ls = this.getLocalStorage('wtt');
    if (ls?.saveState && ls.saveState.tab) {
      // localStorageのsaveStateを削除
      this.deleteLocalStorage('wtt', 'saveState');
    }
  },

  /**
   * 文字列判定
   * @param value
   * @return bool
   */
  isString(inputText) {
    return (typeof inputText === 'string' || inputText instanceof String);
  },


  /**
   * 数値判定（NaNやInfinityは無効）
   * @param value
   * @return bool
   */
  isNumber(value) {
    return Number.isFinite(value);
  },


  /**
   * 整数値の判定
   * @param str
   * @return bool
   */
  checkInteger(str) {
    if (!str) return false;
    return String(str).match(/^[+|-]?[0-9]+$/);
  },


  /**
   * 整数値か小数値の判定
   * @param str
   * @return bool
   */
  checkFloat(str) {
    if (!str) return false;
    return str.match(/^[-|+]?[0-9]*\.[0-9]+$|^[+|-]?[0-9]+$/);
  },


  /**
   * valueが整数値に変更可能かどうか
   * @param value
   * @return bool
   */
  convert2Num(str) {
    if (!str) return null;
    if (!this.checkInteger(str)) return null;
    const result = Number(str);
    return this.isNumber(result) ? result : null;
  },


  /** URLからページ番号取得 */
  getPageNum(route) {
    const query = route.query;
    const page = this.convert2Num(query.page);
    return page || 1;
  },


  /**
   * ページネーション配列の生成
   * @param object args
   *   currentPage
   *   lastPage
   * @return array
   *
   * パターン1
   *   合計ページ数が5未満
   * パターン2
   *   1 2 3 4 ... 50
   * パターン3
   *   1 ... 48 49 50
   * パターン4
   *   1 ... 3 4 5 ... 50
   */
  generatePagination(args) {
    const result = [];
    const last = args.lastPage;
    const prevNext = last > 5;

    // 1ページ完結の場合はスルー
    if (last === 1) return [];

    // prevボタン
    if (prevNext) {
      result.push({
        class: 'prev',
        icon: 'fal fa-angle-left',
        page: args.currentPage - 1,
      });
    }

    // パターン毎にpager作成
    const start = [1, 2, 3];
    const end = [(last - 2), (last - 1), last];
    const num = 4;
    if (!prevNext) {
      for (let i = 0; i < last; i += 1) {
        result.push({ page: i + 1 });
      }
    } else if (start.includes(args.currentPage)) {
      // パターン1
      for (let i = 0; i < num; i += 1) {
        result.push({ page: i + 1 });
      }
      result.push(
        { dot: true },
        { page: last },
      );
    } else if (end.includes(args.currentPage)) {
      // パターン2
      result.push(
        { page: 1 },
        { dot: true },
      );
      for (let i = 0; i < 4; i += 1) {
        result.push({ page: last - (num - i - 1) });
      }
    } else {
      // パターン3
      result.push(
        { page: 1 },
        { dot: true },
        { page: args.currentPage - 1 },
        { page: args.currentPage },
        { page: args.currentPage + 1 },
        { dot: true },
        { page: last },
      );
    }

    // nextボタン
    if (prevNext) {
      result.push({
        class: 'next',
        icon: 'fal fa-angle-right',
        page: args.currentPage + 1,
      });

      // 現ページが最初か最後の場合はdisabledクラス追加
      if (args.currentPage === 1) result[0].class = 'prev disabled';
      if (args.currentPage === last) result[result.length - 1].class = 'next disabled';
    }

    return result;
  },

  /**
   * user_idからuser情報を返却
   */
  async getGuestUser(id) {
    const response = await axios({
      method: 'GET',
      url: '/v1/user/get/byId',
      params: { id },
    });
    return response.data.user;
  },

  /**
   * 環境に応じてstripeのpublicKeyを返却
   */
  getStripePubKey() {
    let STRIPE_PUB_KEY = null;
    switch (process.env.NODE_ENV) {
      case 'production':
        STRIPE_PUB_KEY = process.env.VUE_APP_STRIPE_PUBLIC_KEY_PROD;
        break;
      default:
        STRIPE_PUB_KEY = process.env.VUE_APP_STRIPE_PUBLIC_KEY_DEV;
        break;
    }
    return STRIPE_PUB_KEY;
  },


  /** オンライン健康相談をシフト日付昇順でソート */
  upSortReserves(rows) {
    return rows.sort((a, b) => {
      // firefoxで期待した結果が得られないため先にフォーマットしておく
      const A = moment(a.schedule.date).format('YYYY-MM-DD HH:mm:ss');
      const B = moment(b.schedule.date).format('YYYY-MM-DD HH:mm:ss');
      if (moment(A) < (moment(B))) return -1;
      if (moment(A) > (moment(B))) return 1;
      return 0;
    });
  },

  /** オンライン健康相談をシフト日付降順でソート */
  downSortReserves(rows) {
    return rows.sort((a, b) => {
      // firefoxで期待した結果が得られないため先にフォーマットしておく
      const A = moment(a.schedule.date).format('YYYY-MM-DD HH:mm:ss');
      const B = moment(b.schedule.date).format('YYYY-MM-DD HH:mm:ss');
      if (moment(A) > (moment(B))) return -1;
      if (moment(A) < (moment(B))) return 1;
      return 0;
    });
  },

  /** テキスト健康相談を作成日昇順でソート */
  upSortTexts(rows) {
    return rows.sort((a, b) => {
      const A = moment(a.created_at).format('YYYY-MM-DD HH:mm:ss');
      const B = moment(b.created_at).format('YYYY-MM-DD HH:mm:ss');
      if (moment(A) < moment(B)) return -1;
      if (moment(A) > moment(B)) return 1;
      return 0;
    });
  },

  /** 営業日計算 */
  calcSalesMinDate(holiday) {
    const today = new Date();
    // 基本は３日後
    let result = moment(today);
    const holidays = holiday.list;
    holidays.sort((a, b) => {
      if (a.date < b.date) return -1;
      return 1;
    });
    /** 営業日計算
     * 土日及び祝日の日はcheckNoを追加せず、それ以外の日には追加する
     * これが3営業日になったらそれまでに経過した日数をresultに反映
     * （土日と祝日の処理を順番に処理してしまうと、追加した日数の日に対して処理できなくなる）
     */
    let checkNo = 0;
    let addDay = 0;
    while (checkNo < 3) {
      addDay += 1; // 日数を追加
      const targetDate = moment(today).add(addDay, 'days').startOf('day').format('YYYY-MM-DD');
      const targetDayNo = moment(targetDate).format('e');
      const isHoliday = holidays.some((targetHoliday) => {
        if (targetHoliday.date === targetDate) return true;
        return false;
      });
      if (![0, 6].includes(Number(targetDayNo)) && !isHoliday) {
        checkNo += 1;
      }
    }
    result = result.add(addDay, 'd');
    result = result.toDate();
    return result;
  },
  /**
   * 年度(yyyy)を取得する
   * @param {*} date
   * @returns yyyy
   */
  getFiscalYear(date) {
    return moment(date).clone().add(-3, 'months').year(); // 対象月から3か月前を計算し、その年の部分を抽出する
  },
  /**
   * ユーザ名を整形する
   * @param {*} username
   * @returns 整形後の文字列
   */
  fixUsername(username) {
    let str = username;
    if (!str) {
      return str;
    }
    str = str.replace(/"/g, ''); // csvからの登録では文字列リテラルがついていることがあるので
    if (str.indexOf(' ')) {
      str = str.replace(' ', '');
    } else if (str.indexOf('　')) {
      str = str.replace('　', '');
    }
    return str;
  },
  /**
   * 全角数字を半角数字に変換
   * @param {string} str
   * @returns {string} 半角数字に変換された文字列
   */
  toHalfNumber(str) {
    return str.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
  },

  /**
   * 【重要】
   * コンポーネントで直接使用するには
   * methodsオブジェクトに入れておく必要がある
   */
  methods: {
    /**
     * メッセージ・コメント投稿者の表示名を返す。
     * 事務局(account_type=31)は「事務局」固定。
     * 学校管理者の主(11)/副(12)には「管理者・」「副管理者・」prefix を付与するが、
     * これは閲覧者が管理者側（学校管理者11/12・事務局31）の画面のときのみ。
     * 利用者・医師心理士が閲覧する画面では prefix を付けない（情報最小公開）。
     * account_type 11 は文言統一方針により「主管理者」でなく「管理者」表記
     * （labels.json の account_type ラベル・他画面表記と一致。「副」は識別のため維持）。
     * 予約系の所属なし・閲覧者ゲートなし版は getMemoAuthorName（用途で使い分け）。
     * @param {object} message messages レコード（message.user を含む）
     * @returns {string} 表示名
     */
    getPostUserDisplayName(message) {
      if (!message || !message.user) {
        return '';
      }
      if (message.user.account_type === 31) {
        return '事務局';
      }
      // 管理者/副管理者 prefix は管理者側ビュー（閲覧者=学校管理者11/12・事務局31）でのみ付与。
      const viewerIsAdminSide = !!this.user && [11, 12, 31].includes(this.user.account_type);
      if (viewerIsAdminSide && message.user.account_type === 11) {
        return `管理者・${message.user.username}`;
      }
      if (viewerIsAdminSide && message.user.account_type === 12) {
        return `副管理者・${message.user.username}`;
      }
      return message.user.username;
    },
    /**
     * メッセージ・コメント投稿者の F 情報（schoolAdmins の所属/職階/役割）を
     * 括弧付きで返す。形式: `（所属／職階）`。
     * 【投稿者条件】学校管理者の主(11)/副(12)のみ対象（他 account_type の投稿者は ''）。
     * 【閲覧者条件】管理者側（学校管理者11/12・事務局31）の画面でのみ表示し、
     * 利用者・医師心理士が閲覧する画面では '' を返す（情報最小公開・Counselor に職階を漏らさない）。
     * department/position/role を物理列順で連結する。サイト種別で非表示の列は
     * 登録時に null 固定（schoolAdminFunctions.clearHiddenSchoolAdminFields）のため、
     * 非 null の列＝当該サイト種別の表示項目となり、labels.json のサイト種別ラベル
     * 切替と同じ並び・項目になる（site_type を読まずに同等の結果が得られ、
     * memo 投稿者のように school 未 eager-load な経路でも破綻しない）。
     * 3 列とも未設定なら「（要更新）」を返す（テンプレ側 v-if は常に表示）。
     * @param {object} message messages/memos レコード（message.user.schoolAdmin を含む）
     * @returns {string} `（…）` 形式の F 情報、対象外なら ''
     */
    getPostUserAffiliation(message) {
      // 閲覧者ゲート: 管理者側（学校管理者11/12・事務局31）の画面でのみ F 情報を表示（情報最小公開）。
      // this.user 不在時は false 倒し＝非表示でフェイルセーフ。
      if (!this.user || !AFFILIATION_VIEWER_ACCOUNT_TYPES.includes(this.user.account_type)) {
        return '';
      }
      if (!message || !message.user
        || (message.user.account_type !== 11 && message.user.account_type !== 12)) {
        return '';
      }
      return formatSchoolAdminAffiliation(message.user.schoolAdmin);
    },
    /**
     * メッセージ・コメント投稿者が現在無効化(flag!==1)されているかを返す（#3185）
     * 998=非アクティブ / 999=削除済 を一括で「無効」扱いし、文言は区別しない。
     * 判定述語 flag !== 1 は userBoxColor / RiskApplicationBox と統一。
     * 事務局(account_type=31)は画面上「事務局」固定表示のため抑止（常に false）。
     * flag 未到達(新規送信直後の楽観表示等)は false 寄せ＝過検出を避ける。
     * 過去投稿の本文表示はそのまま、投稿者氏名にラベル/グレー化を付与する用途。
     * @param {object} message messages レコード（message.user を含む）
     * @returns {boolean} 投稿者が無効化されていれば true
     */
    isInactivePostUser(message) {
      if (!message || !message.user) {
        return false;
      }
      if (message.user.account_type === 31) {
        return false;
      }
      const { flag } = message.user;
      return flag != null && Number(flag) !== 1;
    },
    /**
     * reserveMemo（学校／企業コメント）の投稿者表示名を返す。
     * 形式は「管理者・氏名」(account_type=11) /「副管理者・氏名」(12)。
     * account_type 11 は文言統一方針により「主管理者」でなく「管理者」表記とする
     * （labels.json の account_type ラベル・他画面の管理者/副管理者表記と一致。
     * 「副」は識別のため維持）。getPostUserDisplayName と異なり閲覧者ゲートを
     * 設けず、医師心理士(counselor)・学校管理者(teacher)・事務局(operator) いずれの
     * 画面でも同形式で表示する（reserveMemo は職階(F情報)を出さない代わりに、
     * 主/副の区別だけは全サイト統一で出す要件）。MessageDetail の情報最小公開ポリシー
     * （getPostUserDisplayName）には影響しない別ヘルパとして分離している。
     * reserveMemos.teacher は Reserve モデルで account_type ∈ [11,12] のみが振り分けられる
     * ため実質 主(11)/副(12) のいずれかだが、想定外 account_type は氏名のみにフォールバック。
     * @param {object} memo reserveMemos レコード（memo.user を含む）
     * @returns {string} 表示名
     */
    getMemoAuthorName(memo) {
      if (!memo || !memo.user) {
        return '';
      }
      if (memo.user.account_type === 11) {
        return `管理者・${memo.user.username}`;
      }
      if (memo.user.account_type === 12) {
        return `副管理者・${memo.user.username}`;
      }
      return memo.user.username;
    },
    /** 日付の曜日を取得
     *  type:
     *    full => 全表記(例:月曜日)
     *    short => 短縮表記(例:月)
     */
    getWeekDayLabel(date, type) {
      const m = moment(date).locale('ja');
      if (type === 'full') return m.format('dddd');
      if (type === 'short') return m.format('ddd');
      return 'Type Error! Check cf.';
    },

    /** タイムスタンプのフォーマット */
    formatTimestamp(stamp, format = 'YYYY-MM-DD') {
      let result = moment(stamp, 'X').format(format);
      if (!String(stamp).includes('GMT')) {
        result = moment(stamp).format(format);
      }
      return result;
    },

    /** 日付の差分（引数ー本日） */
    getRemainingToday(target) {
      const today = moment(new Date());
      const limit = moment(target);
      const remainingDays = limit.diff(today, 'days') > -1
        ? limit.diff(today, 'days')
        : 0;
      return remainingDays;
    },

    /** クローンオブジェクトの返却 */
    getClone(object) {
      return cloneDeep(object);
    },

    /**
     * アイコンURL（type = 2）の返却
     * @param obj object
     *   ユーザ等特定idのオブジェクト
     */
    getMyIconUrl(object) {
      const defaultIcon = '/img/default/noimage.jpg';
      let url = defaultIcon;

      if (!object) return url;

      const keys = Object.keys(object);
      if (!keys.includes('urls')) return url;

      const isArray = Array.isArray(object.urls);
      if (isArray) {
        // adjustUrls未適用（[{}, {}, ...]）
        const urls = object.urls || [];

        if (urls.length) {
          // item.type = 2（アイコン）画像を返却する
          urls.some((item) => {
            if (item.type === 2) {
              url = item.url || defaultIcon;
              return true;
            }
            return false;
          });
        }
      } else if (object.urls && object.urls.icon.length) {
        // adjustUrls適用済み（{ main: {}, icon: {}, ... }）
        url = object.urls.icon[0].url || defaultIcon;
      }

      return url;
    },

    /**
     * ユーザオブジェクトから表示ラベルを返却
     * // 1. usernameが存在する場合は優先
     * // 2. usernameなくfacebook.nameが存在する場合はfacebook.name
     * // 3. username・facebook.nameともにない場合はemail
     */
    getUserLabel(user) {
      let name = user.email;
      if (user.username) name = user.username;
      return name;
    },

    /**
     * アイキャッチ取得
     * data.eyecatchの有無でデフォ画像と振り分け
     */
    getEyecatch(data) {
      return data.eyecatch || '/img/default/default.jpg';
    },

    /** 文字列の中からimgタグとhtmlタグを除去 */
    replaceImgTag(str) {
      let result = str;
      if (str) {
        // imgタグをsrc丸ごと削除
        const images = str.match(/<img(.|\s)*?>/gi);
        if (images && images.length) {
          images.forEach((img) => {
            result = result.replace(img, '');
          });
        }
        // テキスト文中からhtmlタグを削除
        result = result
          .replace(/(<([^>]+)>)/gi, '')
          .replace(/\n/g, '')
          .replace(/&nbsp;/g, '');
      }
      return result;
    },

    /**
     * 3桁毎にカンマ割り振り
     * @param int num 元の数値
     */
    addComma(num, afterPoint = '', count = 0) {
      if (num !== 0 && !num) return '';
      // if (!this.isNumber(num)) return false;

      // 文字列にする
      let strNum = String(num);
      let afterDecimalPoint = afterPoint;
      if (strNum.includes('.')) {
        // 小数点以下を含む場合
        const split = strNum.split('.');
        strNum = split[0];
        afterDecimalPoint = `.${split[1]}`;
      }
      const len = Number(strNum.length);

      // 再帰的に呼び出し
      if (len > 3) {
        // 前半を引数に再帰呼び出し + 後半3桁
        strNum = `${this.addComma(strNum.substring(0, len - 3), afterDecimalPoint, count + 1)},${strNum.substring(len - 3)}`;
      }

      if (count === 0) {
        strNum += `${afterDecimalPoint}`;
      }

      return `${strNum}`;
    },

    /**
     * 小数点以下の計算をする際に
     * ずれが生じてしまうため、
     * 文字列かして小数点の位置を判別
     */
    getDotPosition(value) {
      // 数値のままだと操作できないので文字列化
      const strVal = String(value);
      let dotPosition = 0;

      // 小数点が存在するか確認
      if (strVal.lastIndexOf('.') !== -1) {
        // 小数点があったら位置を取得
        dotPosition = (strVal.length - 1) - strVal.lastIndexOf('.');
      }

      return dotPosition;
    },

    /**
     * 小数点が存在する数値の合計値計算
     * @param value1  対象1
     * @param value2  対象2
     * @param formula 式
     *   addition 足し算
     *   subtract 引き算
     *   multiply 掛け算
     *   divide   割り算
     */
    decimalCalculation(value1, value2, formula = 'addition') {
      // それぞれの小数点の位置を取得
      const dotPosition1 = this.getDotPosition(value1);
      const dotPosition2 = this.getDotPosition(value2);

      // 位置の値が大きい方（小数点以下の位が多い方）の位置を取得
      const max = Math.max(dotPosition1, dotPosition2);

      // 大きい方に小数の桁を合わせて文字列化、
      // 小数点を除いて整数の値にする
      const intValue1 = parseInt((value1.toFixed(max)).replace('.', ''), 10);
      const intValue2 = parseInt((value2.toFixed(max)).replace('.', ''), 10);

      // 10^N の値を計算
      const power = 10 ** max;

      // 整数値で計算した後に10^Nで割る
      let result;
      switch (formula) {
        case 'addition': // 足し算
          result = (intValue1 + intValue2) / power; break;
        case 'subtract': // 引き算
          result = (intValue1 - intValue2) / power; break;
        case 'multiply': // 掛け算
          result = (intValue1 * intValue2) / power; break;
        case 'divide': // 割り算
          result = (intValue1 / intValue2) / power; break;
        default:
          result = (intValue1 + intValue2) / power; break;
      }
      return result;
    },

    /**
     * 小数点n位で四捨五入
     * @param value
     */
    roundN(value, n) {
      if (!value) return 0;
      const N = 10 ** n;
      return Math.round(value * N) / N;
    },

    /**
     * value1/valu2ををパーセンテージで返却
     * 小数点2位以下を四捨五入
     * @param value1, value2
     */
    getPercentage(value1, value2) {
      const decimalValue = this.decimalCalculation(value1, value2, 'divide');
      return this.roundN(decimalValue * 100, 2);
    },

    /**
     * 回答期限のアラート
     */
    getLimitAlert(limit) {
      let result = { color: '', label: '' };
      const yellow = 'yellow';
      const red = 'red';
      // フォーマットを合わせる必要がある
      const today = moment(new Date()).format('YYYY-MM-DD');
      const mlimit = moment(limit).format('YYYY-MM-DD');
      // 差分を抽出
      const diff = moment(mlimit).diff(moment(today), 'days');

      if (diff === 1) {
        result = {
          color: yellow,
          label: '締切前日',
        };
      } else if (diff === 0) {
        result = {
          color: red,
          label: '締切当日',
        };
      } else if (diff < 0) {
        result = {
          color: red,
          label: '締切日超過',
        };
      }
      return result;
    },

    /**
     * filter群
     */
    filterMoment(value, format) {
      return moment(value).format(format);
    },
    /**
     * 予約日時の日本時間表記。画面の予約日時は端末の時刻で表示する方針なので、
     * 海外の端末ではメール（日本時間）と数字が食い違う。確認画面・完了 alert・予約詳細でこれを添える。
     * 判定はその日時時点の端末オフセットで行う（夏時間のある地域でも当日の時差で決まる）
     * @param str value 予約日時（schedules.date の ISO 文字列）
     * @return str メールと同じ書式（例: '2026年09月08日（火） 21:00 〜 21:45'）。端末が日本時間・ISO でない値は ''
     */
    jstSlotLabel(value) {
      return formatJstSlot(value);
    },
    /** alert 用。端末が日本時間以外のときだけ「（お住まいの地域の時刻。日本時間では …）」を返す */
    jstSlotSuffix(value) {
      const label = formatJstSlot(value);
      return label ? `（お住まいの地域の時刻。日本時間では ${label}）` : '';
    },
    /** 予約枠の終了時刻（端末の時刻）。書式に ':45' を固定で書くと、30 分ずれの地域（インド等）で誤る */
    slotEndTime(value) {
      return moment(value).add(COUNSELING_SLOT_MINUTES, 'minutes').format('HH:mm');
    },
    filterDate(date) {
      return moment(date).format('YYYY.M.D');
    },
    filterDate_ja(date) {
      return moment(date).format('YYYY年M月D日');
    },
    filterNumber_format(value) {
      // valueを文字列に変換
      const str = String(value);
      if (!str.match(/^\d+$/)) {
        return value;
      }
      const formatter = new Intl.NumberFormat('ja-JP');
      return formatter.format(str);
    },
    filterZip(value) {
      const str = value ? String(value) : null;
      let result = '';
      if (str && str.includes('-')) {
        // ハイフンが含まれている場合はそのまま出力
        result = `〒${str}`;
      } else if (str && !str.includes('-')) {
        // ハイフンを付与して出力
        result = `〒${str.substr(0, 3)}-${str.substr(3, 4)}`;
      }
      return result;
    },
    filterRemove_hyphen(value) {
      const str = value.replace(/-/g, '');
      return str;
    },
    filterDay(str) {
      return moment(str).day();
    },

    /**
     * オンライン健康相談の同時刻上限（= Zoom アカウント実数 N）に達しているかを判定する。
     * 一覧の満席バッジ（SelectCounselor.isOnlineSlotFull）・backend の onlineReserveFull と同一述語
     * （counseling_type=1 の有効予約を予約単位で数える集計 /v2/reserve/get/onlineReserveCounts）を
     * 単一情報源として用いる。満杯なら alert して false を返す。
     * ※オンライン専用の判定。バーチャルは「同時刻 1 件で重複」で別ロジックのため、呼出側で online に限定して呼ぶこと。
     * @param startDate 予定開始時刻
     * @param limit 同時刻上限（呼び出し元が helper.master.onlineSameTimeLimit を渡す。未指定時は安全側の 2）
     * @returns 予約可能な場合は true, それ以外は false
     */
    async checkOnlineReserveTime(startDate, limit = 2) {
      // 満席の数え方を一覧バッジ(B)・backend(C)と揃えるため、schedule+hasOne の JS 判定ではなく
      // 集計エンドポイント（counseling_type=1・予約単位 distinct の count）を単一情報源として引く。
      // 本メソッドは cf.methods.checkOnlineReserveTime(...) としてコンポーネント外文脈でも呼ばれ
      // this.helper が使えないため、上限値は引数で受ける。
      try {
        const response = await axios({
          method: 'GET',
          url: '/v2/reserve/get/onlineReserveCounts',
          params: {
            // 端末 now でなく JST 日付でカウント照会（非 JST 端末の日付境界ずれを防ぐ）。
            scheduleStartDate: moment().utcOffset(JST_UTC_OFFSET_MIN).format('YYYY-MM-DD'),
          },
        });
        const counts = response.data.counts || [];
        // c.date はサーバの naive JST 文字列（DATE_FORMAT '%Y-%m-%d %H:%i:%s'）、startDate(schedule.date) は
        // ISO-UTC。素の moment(c.date) は端末 TZ 解釈で非 JST 端末では別インスタントになる（満席判定の空振り）。
        // c.date を JST 壁時計として固定（utcOffset(..., true)=keepLocalTime）し、インスタント比較を TZ 非依存にする。
        const entry = counts.find((c) => moment(c.date).utcOffset(JST_UTC_OFFSET_MIN, true).isSame(moment(startDate)));
        if (entry && entry.count >= limit) {
          // 「満席」「枠」は利用者が認知していない語のため使わず、平易な言い回しにする（定例方針）。
          alert('この時間帯は、ほかの方のご予約が入ったため、お選びいただけません。お手数ですが、別の日時をお選びください。');
          return false;
        }
        return true;
      } catch (error) {
        console.error(error);
        alert('予約済みのオンライン相談情報の取得に失敗しました。');
        throw error;
      }
    },

    // textareaでの文字列をhtml化する
    convertText2Html(text) {
      let notice = text;
      // notice = notice.replace(/\r\n/g, '&lt;br /&gt;<br />');
      notice = notice.replace(/(\n|\r)/g, '<br />');
      return `<p>${notice}</p>`;
    },

    // パーセンテージを算出
    calcPercentage(target, all) {
      return Math.trunc(Math.round((target / all) * 100));
    },

    //パーセンテージを指定した小数点以下まで算出 デフォルトは小数点以下1桁まで
    calcPercentageDecimal(target, all, digits = 1) {
      return Math.round((target / all) * 100 * (10 ** digits)) / (10 ** digits);
    },

    //指定した少数点数で切り上げ
    calcCeilDecimal(target, all, digits = 1) {
      return Math.ceil((target / all) * 100 * (10 ** digits)) / (10 ** digits);
    },

    /** バーチャルキャストのユーザURLを取得する */
    getVirtualCastUserURL(vcid) {
      return `https://virtualcast.jp/users/${vcid}`;
    },

    /** バーチャルキャストのルームURLを取得する */
    getVirtualCastMedicalRooomURL(roomKey) {
      return `https://virtualcast.jp/rooms/${roomKey}`;
    },

    /** userBox背景色指定用 */
    userBoxColor(user) {
      if (!user || !user.flag) return 'white'; // ユーザが存在しない、またはflag自体が不明の場合はデフォルトの白を返す
      let color;
      if (user.school && user.school.length && user.school[0].flag !== 1) {
        // 所属校が無効な場合はグレー
        color = 'gray';
      } else if (user.flag !== 1) {
        // ユーザが無効な場合はグレー
        color = 'gray';
      } else if (user.temporary) {
        // 一時ユーザの場合はシルバー
        color = 'silver';
      } else if (user.risk && user.risk.length && user.risk[user.risk.length - 1].type !== 11) {
        // リスクが存在し、かつ最新のリスクが11（問題なし（回復））でない場合は赤
        color = 'red';
      } else {
        color = 'white';
      }
      return color;
    },

    // キャンセルを実行したユーザのアカウントタイプ
    getAccountTypeLabel(reserve) {
      let result = '';
      let accountType;
      const labels = this.helper.master.labels;
      if (reserve.cancel_user_id) {
        // 240215 cancel_user_id追加後はキャンセルユーザが直でわかる
        accountType = reserve.cancelUser.account_type || null;
      } else {
        // それ以前の予約はreasonおよびメモからキャンセル実行者を判定
        // 学校管理者・事務局は判定不可
        const reason = reserve.cancel_reason;
        if (reason === 4) {
          // 4（利用者現れない）は医師・心理士がキャンセル実行
          accountType = reserve.toUser.account_type;
        } else {
          const cancelMemo = reserve.memos.cancel;
          const hasMemo = cancelMemo.length;
          accountType = hasMemo ? cancelMemo[0].user.account_type : null;
        }
      }
      // 相談者の所属する学校が企業アカウント（is_company=1）かどうかを判定
      const hasSchool = reserve.fromUser?.school?.length > 0;
      const isCompany = hasSchool && reserve.fromUser?.school?.[0]?.is_company;

      // 利用者の最大アカウントタイプ（11以上は心理士など）
      const ACCOUNT_TYPE_USER_MAX = 10;

      // 企業アカウントの場合は企業用ラベルを使用（表示するアカウントタイプが利用者の場合に適用）
      const accountTypeLabels = isCompany && accountType <= ACCOUNT_TYPE_USER_MAX
        ? labels.users.company_account_type
        : labels.users.account_type;

      result = accountType ? `（実行者: ${accountTypeLabels[accountType]}）` : '';
      return result;
    },
    /**
     * お悩み（相談種別）の type 番号を表示ラベルに変換する。
     * 所属校が企業アカウント（is_company=1）の場合は企業向けラベル（type_company）を、
     * それ以外は学校向けラベル（type）を返す。type 番号（保存値）は表示層でのみ写像し変更しない。
     * 企業ブロック未定義時は学校向けにフォールバックする（後方互換）。
     * ※ 名称は RiskApplicationForm のローカル getCounselingTypeLabel（相談種別＝別概念）との
     *   衝突を避けるため getWorryTypeLabel とする。
     * @param {number|string} typeNum 保存値の type 番号（1-6）
     * @param {boolean} isCompany 所属校が企業アカウントか
     * @param {string} block labels.json のブロック名（'reserve' | 'texts'）
     * @returns {string} 表示ラベル
     */
    getWorryTypeLabel(typeNum, isCompany, block = 'reserve') {
      const labels = this.helper.master.labels;
      const group = labels?.[block];
      if (!group) return '';
      const labelMap = isCompany && group.type_company ? group.type_company : group.type;
      return labelMap?.[typeNum] ?? '';
    },
    /**
     * お悩みの大項目 type 配列をマスタ番号昇順に並べて返す。
     * type 配列はフォームでチェックした順に保存されるため番号順とは限らない。
     * 保存値は変更せず、表示順だけを正すために表示側の v-for で使う。
     * @param {Array<number|string>} types 大項目 type 配列
     * @returns {Array<number>} 番号昇順にソートした配列（配列でなければ空配列）
     */
    sortWorryTypes(types) {
      if (!Array.isArray(types)) return [];
      return [...types].map(Number).sort((a, b) => a - b);
    },
    /**
     * 指定メイン番号配下の「選択済み」サブ項目ラベルを ' / ' 連結して返す。
     * 予約入力の確認ステップ（ReserveFormWizard の worrySummary）と同じ表示形へ全画面を揃えるための共有 helper。
     * 選択0件 / 非企業 / master にサブ定義なし のときは空文字を返す。呼び出し側は v-if="" で行ごと出さない
     * （空文字を返すこと自体が0件ガードの実体。span を空で残すと margin だけが出て崩れる）。
     * 第3引数は呼び出し側が「企業（is_company かつ is_corporate）」判定を渡す契約。
     * is_company 単独（非学校）に巻き戻すとインターンでもサブが出る（要件違反）ため、
     * corporateSchool mixin の isCorporateSchool 由来の値を渡すこと。
     * @param {number|string} typeNum メイン番号（1-5。6はサブを持たない）
     * @param {Array<number|string>} selectedSubs 選択済みサブ番号の配列（undefined/null可）
     * @param {boolean} isCorporate 企業（is_company かつ is_corporate）か
     * @param {string} block labels.json のブロック名（'reserve' | 'texts'）
     * @returns {string} 例: '上司との関係 / 報告・連絡・相談'（該当なしは ''）
     */
    getWorrySelectedSubText(typeNum, selectedSubs, isCorporate, block = 'reserve') {
      if (!isCorporate) return '';
      const subMap = this.helper?.master?.labels?.[block]?.type_company_sub?.[typeNum];
      if (!subMap) return '';
      const selected = Array.isArray(selectedSubs) ? selectedSubs.map(Number) : [];
      return Object.keys(subMap)
        .map(Number)
        .sort((a, b) => a - b) // 数値昇順。辞書順だと 10 < 2 になる
        .filter((subNum) => selected.includes(subNum))
        .map((subNum) => subMap[subNum])
        .join(' / ');
    },
    /**
     * counseling_types レコードに sub_type が保存されているかを判定する。
     * バック側で sub_type はパース済みオブジェクト or null として返る。
     * @param {object} content reserve / messageGroup の content 相当（_counselingType か type プロパティを持つ）
     * @returns {boolean}
     */
    hasSubTypesRecorded(content) {
      const subTypes = content?._counselingType?.sub_type || content?.type?.sub_type;
      return !!subTypes && typeof subTypes === 'object' && Object.keys(subTypes).length > 0;
    },
    /**
     * 指定 content の指定メイン番号配下に保存されているサブ番号配列を返す。
     * @param {object} content reserve / messageGroup の content 相当
     * @param {number|string} typeNum メイン番号
     * @returns {Array<number>}
     */
    getRecordedSubs(content, typeNum) {
      const subTypes = content?._counselingType?.sub_type || content?.type?.sub_type;
      if (!subTypes) return [];
      const list = subTypes[typeNum] || subTypes[String(typeNum)] || [];
      return Array.isArray(list) ? list.map(Number) : [];
    },
    /**
     * 検索後のハイライトを意図的に除去して返却
     * デフォルトで数値型にして返すが、第二引数にstrなどが指定されていれば文字列で返す
     */
    replaceHighlight(mixedId, type = 'num') {
      let result = mixedId;
      //　変換が必要ないケース
      if (typeof mixedId === 'number' && type === 'num') return result;
      // spanタグ除去
      if (mixedId.includes('<span')) {
        // 氏名などは印が複数付くことがあるので、最初の 1 個だけでなく全部外す
        result = mixedId.split('<span class="highlight">').join('').split('</span>').join('');
        if (type === 'num') Number(result);
      }
      return result;
    },

    /**
     * オンライン健康相談の相談内容が存在するか判定し返却
     */
    getCustomerMemo(reserveCustomerMemo) {
      let result = '記入なし';
      if (
        reserveCustomerMemo.length
          && reserveCustomerMemo[0].memo
          && reserveCustomerMemo[0].memo !== ''
      ) result = reserveCustomerMemo[0].memo;
      return result;
    },

    /** 文字列がメールアドレスの形式に合致しているか */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    /**
     * TinyMCEで編集されたHTMLコンテンツをクリーンアップして画面表示用に最適化
     * ブロック要素間の改行・空白を除去し、.n2brのwhite-space: pre-wrapによる不要な空白表示を防ぐ
     * @param {string} content - HTMLコンテンツ
     * @returns {string} クリーンアップされたHTMLコンテンツ
     */
    trimTagWhitespace(content) {
      if (!content) return '';
      // タグ間の改行・空白を削除
      // ユーザーが意図的に入れた空行は<div>&nbsp;</div>や<div><br></div>として保持される
      return content.replace(/>\s+</g, '><');
    },
  },
};
